

import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';


import { PanelImageView } from './PanelImageView';

import type { StoryboardPanel } from '@/lib/storyboard-types';

interface PanelCardProps {
  panel: StoryboardPanel;
  onUpdate: (updates: Partial<StoryboardPanel>) => void;
  onRegenerate: (changeRequest?: string) => void;
  // Image props
  isGeneratingImage?: boolean;
  onGenerateImage?: () => void;
  onRefineImage?: (changeRequest: string) => void;
  onAcceptImage?: (iterationIndex: number) => void;
  isTokenDepleted?: boolean;
}

export function PanelCard({
  panel,
  onUpdate,
  onRegenerate,
  isGeneratingImage = false,
  onGenerateImage,
  onRefineImage,
  onAcceptImage,
  isTokenDepleted = false,
}: PanelCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [changeRequest, setChangeRequest] = useState(panel.changeRequest || '');

  const isAccepted = panel.scriptStatus === 'accepted';
  const needsChanges = panel.scriptStatus === 'needs_changes';

  const handleAccept = () => {
    onUpdate({ scriptStatus: 'accepted', changeRequest: undefined });
    setIsEditing(false);
    setChangeRequest('');
  };

  const handleRequestChanges = () => {
    if (changeRequest.trim()) {
      onUpdate({ scriptStatus: 'needs_changes', changeRequest: changeRequest.trim() });
      setIsEditing(false);
    }
  };

  const handleRegenerate = () => {
    onRegenerate(changeRequest || undefined);
    setChangeRequest('');
    setIsEditing(false);
  };

  const handleUnlock = () => {
    onUpdate({ scriptStatus: 'pending', changeRequest: undefined });
  };

  const getStatusColor = (): 'success' | 'warning' | 'default' => {
    if (isAccepted) return 'success';
    if (needsChanges) return 'warning';
    return 'default';
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 2,
        border: 2,
        borderColor: isAccepted ? 'success.main' : needsChanges ? 'warning.main' : 'divider',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      {/* Panel number badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          left: 12,
          bgcolor: 'background.paper',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {t('storyboard.panel_card.panel_number', { order: panel.order })}
        </Typography>
      </Box>

      {/* Lock indicator for accepted panels */}
      {isAccepted && (
        <Box sx={{ position: 'absolute', top: -10, right: 12 }}>
          <Tooltip title={t('storyboard.panel_card.unlock_tooltip')}>
            <IconButton size="small" onClick={handleUnlock} sx={{ bgcolor: 'success.light' }}>
              <LockIcon fontSize="small" color="success" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <CardContent sx={{ pt: 3, pb: 1, flex: 1 }}>
        {/* Plot Intent */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {t('storyboard.panel_card.plot_intent')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {panel.plotIntent}
          </Typography>
        </Box>

        {/* Visual Description */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <CameraAltIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {t('storyboard.panel_card.visual')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {panel.visualDescription}
          </Typography>
          {panel.camera && (
            <Chip
              label={panel.camera}
              size="small"
              sx={{ mt: 0.5, mr: 0.5, maxWidth: 'none', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
              variant="outlined"
            />
          )}
          {panel.composition && (
            <Chip
              label={panel.composition}
              size="small"
              sx={{ mt: 0.5, maxWidth: 'none', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
              variant="outlined"
            />
          )}
        </Box>

        {/* Dialogue/Captions */}
        {(panel.dialogue || panel.captions || panel.sfx) && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t('storyboard.panel_card.text')}
              </Typography>
            </Box>
            {panel.dialogue && (
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                "{panel.dialogue}"
              </Typography>
            )}
            {panel.captions && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {panel.captions}
              </Typography>
            )}
            {panel.sfx && (
              <Chip label={t('storyboard.panel_card.sfx', { sfx: panel.sfx })} size="small" color="secondary" sx={{ mt: 0.5 }} />
            )}
          </Box>
        )}

        {/* Characters */}
        {panel.characters.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {panel.characters.map((char, i) => (
              <Chip key={i} label={char} size="small" variant="outlined" />
            ))}
          </Box>
        )}

        {/* Status chip */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Chip
            label={isAccepted ? t('storyboard.panel_card.status.accepted') : needsChanges ? t('storyboard.panel_card.status.needs_changes') : t('storyboard.panel_card.status.pending')}
            size="small"
            color={getStatusColor()}
            variant={isAccepted ? 'filled' : 'outlined'}
          />
        </Box>
      </CardContent>

      <Divider />

      {/* Change Request Input (collapsible) */}
      <Collapse in={isEditing}>
        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder={t('storyboard.panel_card.describe_changes_placeholder')}
            value={changeRequest}
            onChange={(e) => setChangeRequest(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={handleRequestChanges} disabled={!changeRequest.trim()}>
              {t('storyboard.panel_card.save_request')}
            </Button>
            <Tooltip title={isTokenDepleted ? t('storyboard.panel_card.tokens_exhausted') : ''} placement="top">
              <div style={{ display: 'inline-block' }}>
                <Button size="small" variant="contained" color="warning" onClick={handleRegenerate} disabled={isTokenDepleted}>
                  {t('storyboard.panel_card.regenerate')}
                </Button>
              </div>
            </Tooltip>
            <Button size="small" onClick={() => setIsEditing(false)}>
              {t('storyboard.panel_card.cancel')}
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Action Buttons */}
      {!isAccepted && !isEditing && (
        <Box sx={{ display: 'flex', p: 1, gap: 0.5 }}>
          <Tooltip title={t('storyboard.panel_card.accept_tooltip')}>
            <IconButton size="small" color="success" onClick={handleAccept}>
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('storyboard.panel_card.request_changes_tooltip')}>
            <IconButton size="small" color="warning" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isTokenDepleted ? t('storyboard.panel_card.tokens_exhausted') : t('storyboard.panel_card.regenerate')}>
            <span>
              <IconButton size="small" onClick={() => onRegenerate()} disabled={isTokenDepleted}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Image Generation Section */}
      {onGenerateImage && onRefineImage && onAcceptImage && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 1, display: 'block' }}>
              {t('storyboard.panel_card.illustration')}
            </Typography>
            <PanelImageView
              panel={panel}
              isGenerating={isGeneratingImage}
              onGenerate={onGenerateImage}
              onRefine={onRefineImage}
              onAccept={onAcceptImage}
              isTokenDepleted={isTokenDepleted}
            />
          </Box>
        </>
      )}
    </Card>
  );
}
